import { memberService } from "./members";
import { groupService } from "./groups";
import { collectionService } from "./collections";
import { payoutService } from "./payouts";
import type { SearchResult } from "./types";

/**
 * Global search across members (name/phone/code), groups (name/code), receipts
 * (receipt number) and payout vouchers (voucher number). Results are grouped by
 * type with a navigation target each, so staff who only have a document number
 * written on a slip of paper can jump straight to the record.
 */

export async function searchAll(query: string): Promise<SearchResult[]> {
  const q = query.trim();
  if (!q) return [];

  const results: SearchResult[] = [];

  // Members — name, phone digits or member code.
  try {
    const members = await memberService.searchMembers(q);
    for (const m of members.slice(0, 5)) {
      results.push({
        type: "member",
        label: m.fullName,
        sublabel: `${m.memberCode} · ${m.primaryPhone}`,
        link: `/members/${m.id}`,
      });
    }
  } catch {
    // ignore — search degrades gracefully
  }

  // Groups — name or code.
  try {
    const groups = await groupService.listGroups({ search: q });
    for (const g of groups.slice(0, 5)) {
      results.push({
        type: "group",
        label: g.name,
        sublabel: `${g.groupCode} · ${g.status}`,
        link: `/groups/${g.id}`,
      });
    }
  } catch {
    // ignore
  }

  // Receipts — by receipt number.
  try {
    const receipts = await collectionService.listReceipts();
    const matched = receipts.filter((r) => r.receiptNumber.toLowerCase().includes(q.toLowerCase()));
    for (const r of matched.slice(0, 5)) {
      results.push({
        type: "receipt",
        label: r.receiptNumber,
        sublabel: `${r.memberName} · ${r.receivedOn}`,
        link: `/collection/receipts/${r.id}`,
      });
    }
  } catch {
    // ignore
  }

  // Payout vouchers — by voucher number.
  try {
    const payouts = await payoutService.listPayouts();
    const matched = payouts.filter((p) => p.voucherNumber.toLowerCase().includes(q.toLowerCase()));
    for (const p of matched.slice(0, 5)) {
      results.push({
        type: "voucher",
        label: p.voucherNumber,
        sublabel: `${p.winnerName} · ${p.groupName} C${p.cycle}`,
        link: `/payout/${p.id}`,
      });
    }
  } catch {
    // ignore
  }

  return results;
}

export const searchService = {
  searchAll,
};